/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, Input, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NotificationService } from '@alfresco/adf-core';
import { PageHeaderComponent, PageStateComponent } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { JobDefinition, JobRun, JobScheduleState, JobsService } from './jobs.service';
import { JobDialogComponent } from './job-dialog.component';
import { JobRunDialogComponent } from './job-run-dialog.component';
import { DmsMetadataService } from '../upload/dms-metadata.service';

/**
 * Scheduled Jobs (งานอัตโนมัติ) — the administration screen for the
 * `aca-dms-jobs` sidecar. Jobs are JSON files in Data Dictionary/DMS Jobs;
 * this page edits them, asks the sidecar when each will next run, and lists
 * the run reports the service writes back. Everything the expiry script used
 * to do is one job here.
 */
@Component({
  selector: 'app-jobs-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatSlideToggleModule,
    PageHeaderComponent,
    PageStateComponent,
    TranslatePipe
  ],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './jobs-page.component.html',
  styleUrl: './jobs-page.component.scss'
})
export class JobsPageComponent implements OnInit {
  private readonly jobs = inject(JobsService);
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);

  @Input() embedded = false;

  readonly loading = signal(true);
  readonly failed = signal('');
  readonly defs = signal<JobDefinition[]>([]);
  readonly states = signal<Record<string, JobScheduleState>>({});
  readonly runs = signal<JobRun[]>([]);
  readonly runsLoaded = signal(false);
  readonly serviceUp = signal<boolean | null>(null);
  readonly mailOn = signal(false);
  readonly busyId = signal('');
  tab: 'jobs' | 'history' = 'jobs';

  ngOnInit(): void {
    this.load();
    this.jobs.health().subscribe((h) => {
      this.serviceUp.set(h !== null && h.status === 'ready');
      this.mailOn.set(!!h?.mail);
    });
  }

  load(): void {
    this.loading.set(true);
    this.failed.set('');
    this.jobs.list().subscribe({
      next: (defs) => {
        this.defs.set(defs);
        this.loading.set(false);
        this.loadStates();
      },
      error: () => {
        this.loading.set(false);
        this.failed.set(this.translate.instant('DMS.JOBS.LOAD_FAIL'));
      }
    });
  }

  /** Last run per job + next run + running flags, folded into the table. */
  private loadStates(): void {
    this.jobs.scheduleState().subscribe((list) => this.states.set(Object.fromEntries(list.filter((s) => s.id).map((s) => [s.id, s]))));
    this.jobs.runs().subscribe((runs) => {
      this.runs.set(runs);
      this.runsLoaded.set(true);
    });
  }

  stateOf(job: JobDefinition): JobScheduleState | undefined {
    return job.id ? this.states()[job.id] : undefined;
  }

  /** The most recent report for a job, if any. */
  lastRun(job: JobDefinition): JobRun | undefined {
    return this.runs().find((r) => r.jobId === job.id);
  }

  // ---- labels ------------------------------------------------------------

  private static readonly FIELD_LABELS: Record<string, string> = {
    'cm:created': 'DMS.JOBS.F_CREATED',
    'cm:modified': 'DMS.JOBS.F_MODIFIED',
    'cm:to': 'DMS.JOBS.F_EXPIRY',
    'cm:from': 'DMS.JOBS.F_FROM',
    'cm:name': 'DMS.JOBS.F_NAME',
    'cm:title': 'DMS.JOBS.F_TITLE',
    'cm:creator': 'DMS.JOBS.F_CREATOR',
    'cm:modifier': 'DMS.JOBS.F_MODIFIER',
    'cm:owner': 'DMS.JOBS.F_OWNER',
    mimetype: 'DMS.JOBS.F_MIMETYPE',
    size: 'DMS.JOBS.F_SIZE',
    TYPE: 'DMS.JOBS.F_TYPE',
    ASPECT: 'DMS.JOBS.F_ASPECT',
    TAG: 'DMS.JOBS.F_TAG',
    ANCESTOR: 'DMS.JOBS.F_ANCESTOR',
    'dms:documentType': 'DMS.JOBS.F_DOCUMENTTYPE',
    'dms:documentCode': 'DMS.JOBS.F_DOCUMENTCODE',
    'dms:department': 'DMS.JOBS.F_DEPARTMENT',
    'dms:documentOwner': 'DMS.JOBS.F_DOCUMENTOWNER',
    'dms:status': 'DMS.JOBS.F_STATUS',
    'dms:confidentiality': 'DMS.JOBS.F_CONFIDENTIALITY',
    'dms:documentDate': 'DMS.JOBS.F_DOCUMENTDATE'
  };

  fieldLabel(field: string): string {
    const key = JobsPageComponent.FIELD_LABELS[field];
    return key ? this.translate.instant(key) : field;
  }

  /** A rule row as a short readable phrase for the table's condition column. */
  ruleLabel(rule: { field: string; op: string; value?: string; valueTo?: string }): string {
    const field = this.fieldLabel(rule.field);
    const v =
      rule.field === 'dms:documentType' || rule.field === 'dms:status' || rule.field === 'dms:confidentiality'
        ? this.translate.instant(DmsMetadataService.label(rule.value ?? ''))
        : (rule.value ?? '');
    const days = this.translate.instant('DMS.JOBS.DAYS');
    switch (rule.op) {
      case 'equals':
        return `${field} = ${v}`;
      case 'notEquals':
        return `${field} ≠ ${v}`;
      case 'contains':
        return `${field} ~ ${v}`;
      case 'exists':
        return this.translate.instant('DMS.JOBS.OP_EXISTS', { field });
      case 'missing':
        return this.translate.instant('DMS.JOBS.OP_MISSING', { field });
      case 'olderThanDays':
        return this.translate.instant('DMS.JOBS.OP_OLDER', { field, days: rule.value, unit: days });
      case 'newerThanDays':
        return this.translate.instant('DMS.JOBS.OP_NEWER', { field, days: rule.value, unit: days });
      case 'before':
        return this.translate.instant('DMS.JOBS.OP_BEFORE', { field, value: rule.value });
      case 'after':
        return this.translate.instant('DMS.JOBS.OP_AFTER', { field, value: rule.value });
      case 'between':
        return `${field}: ${rule.value} – ${rule.valueTo}`;
      case 'greaterThan':
        return `${field} > ${v}`;
      case 'lessThan':
        return `${field} < ${v}`;
      case 'inList':
        return `${field} ∈ ${v}`;
      default:
        return `${field} ${rule.op} ${v}`;
    }
  }

  /** "0 0 2 * * *" → a short human label for the common presets, else the cron itself. */
  scheduleLabel(job: JobDefinition): string {
    const cron = job.schedule?.cron?.trim() ?? '';
    const presets: Record<string, string> = {
      '0 * * * *': 'DMS.JOBS.SCHED_HOURLY',
      '0 0 * * *': 'DMS.JOBS.SCHED_DAILY0',
      '0 0 2 * * *': 'DMS.JOBS.SCHED_DAILY2',
      '0 0 2 * * 1': 'DMS.JOBS.SCHED_WEEKLY',
      '0 0 2 1 * *': 'DMS.JOBS.SCHED_MONTHLY'
    };
    const key = presets[cron];
    return key ? this.translate.instant(key) : cron;
  }

  actionLabel(type: string): string {
    return this.translate.instant(`DMS.JOBS.ACT_${type.toUpperCase()}`);
  }

  dateLabel(iso?: string | null): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : (this.datePipe.transform(d, 'dd/MM/yyyy HH:mm') ?? '—');
  }

  runResult(run: JobRun): string {
    return this.translate.instant('DMS.JOBS.RUN_SUMMARY', {
      processed: run.processed,
      matched: run.matched,
      errors: run.errors?.length ?? 0
    });
  }

  // ---- actions -----------------------------------------------------------

  add(): void {
    this.dialog
      .open(JobDialogComponent, { width: '720px', maxWidth: '96vw', autoFocus: false })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.notification.showInfo(this.translate.instant('DMS.JOBS.SAVED'));
          this.load();
        }
      });
  }

  /** Opens the full report for one run — query, counters, errors, sample. */
  viewRun(run: JobRun): void {
    this.dialog.open(JobRunDialogComponent, { width: '640px', maxWidth: '96vw', autoFocus: false, data: { run } });
  }

  edit(job: JobDefinition): void {
    this.dialog
      .open(JobDialogComponent, { width: '720px', maxWidth: '96vw', autoFocus: false, data: { job } })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.notification.showInfo(this.translate.instant('DMS.JOBS.SAVED'));
          this.load();
        }
      });
  }

  toggle(job: JobDefinition): void {
    if (!job.id) {
      return;
    }
    const next = { ...job, enabled: !job.enabled };
    this.busyId.set(job.id);
    this.jobs.save(next).subscribe({
      next: () => {
        this.busyId.set('');
        job.enabled = next.enabled;
      },
      error: () => {
        this.busyId.set('');
        this.notification.showError(this.translate.instant('DMS.JOBS.SAVE_FAIL'));
      }
    });
  }

  runNow(job: JobDefinition): void {
    const id = job.id;
    if (!id) {
      return;
    }
    this.busyId.set(id);
    this.jobs.runNow(id).subscribe({
      next: () => {
        this.busyId.set('');
        this.notification.showInfo(this.translate.instant('DMS.JOBS.RUN_STARTED'));
        // the run lands asynchronously; keep re-reading until it finishes
        this.refreshAfterRun(id, 0);
      },
      error: (e) => {
        this.busyId.set('');
        const msg = e?.error?.message;
        this.notification.showError(typeof msg === 'string' ? msg : this.translate.instant('DMS.JOBS.RUN_FAIL'));
      }
    });
  }

  /**
   * The sidecar drops the job's running flag only after the run — report file
   * included — is done, so poll the schedule state and refresh the history
   * table until it clears. Gives up after ~5 minutes of a still-running job.
   */
  private refreshAfterRun(jobId: string, attempt: number): void {
    setTimeout(
      () => {
        this.jobs.scheduleState().subscribe((list) => {
          this.states.set(Object.fromEntries(list.filter((s) => s.id).map((s) => [s.id, s])));
          this.jobs.runs().subscribe((runs) => {
            this.runs.set(runs);
            this.runsLoaded.set(true);
          });
          if (attempt < 60 && list.some((s) => s.id === jobId && s.running)) {
            this.refreshAfterRun(jobId, attempt + 1);
          }
        });
      },
      attempt === 0 ? 3000 : 5000
    );
  }

  remove(job: JobDefinition): void {
    if (!job.id || !confirm(this.translate.instant('DMS.JOBS.DELETE_CONFIRM', { name: job.name }))) {
      return;
    }
    this.busyId.set(job.id);
    this.jobs.remove(job.id).subscribe({
      next: () => {
        this.busyId.set('');
        this.notification.showInfo(this.translate.instant('DMS.JOBS.DELETED'));
        this.load();
      },
      error: () => {
        this.busyId.set('');
        this.notification.showError(this.translate.instant('DMS.JOBS.SAVE_FAIL'));
      }
    });
  }

  trackById(_i: number, job: JobDefinition): string {
    return job.id ?? job.name;
  }

  trackByRun(_i: number, run: JobRun): string {
    return run.nodeId ?? run.startedAt;
  }
}
